import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import type { GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SearchIcon from "@mui/icons-material/Search";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { setMessage, useMaterialUIController } from "context";
import { useAccess } from "hooks/useAccess";
import { cpfApplyMask } from "helpers/utils";
import { canManageAccess, labelRole } from "helpers/accessControlLabels";
import { ROLES } from "helpers/permissions";
import { LojaDataGrid, LojaPageContainer } from "layouts/pages/loja/common";
import {
  getAccessControlUsers,
  getRolesWithPermissions,
  getUserAccessDetail,
  type RoleWithPermissions,
  type SystemUser,
  type UserAccessDetail,
} from "services/rolesPermissions";
import UserAccessDialog from "./components/UserAccessDialog";
import {
  ACESSO_ACCENT,
  actionIconSx,
  acessoToolbarSx,
  emptyStateSx,
  fieldSx,
  gridSx,
  infoBannerSx,
  saveButtonSx,
} from "./controle-acesso.theme";
import {
  computePageStats,
  roleChipSx,
  roleTypesFromUser,
  userHasPanelAccess,
} from "./components/acesso.helpers";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? ACESSO_ACCENT : "text.primary"}
        sx={{ fontSize: { xs: "1.25rem", sm: "2rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function ControleAcessoPage(): JSX.Element {
  const [, dispatch] = useMaterialUIController();
  const access = useAccess();
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [rolesCatalog, setRolesCatalog] = useState<RoleWithPermissions[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [selectedAccess, setSelectedAccess] = useState<UserAccessDetail | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);

  const isAdminManager = canManageAccess(access.roles, access.isSuperAdmin);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAccessControlUsers({
        search: activeSearch || undefined,
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      });
      setUsers(data.results);
      setRowCount(data.totalRecord);
      setIsSearchMode(data.isSearch);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: err?.response?.data?.message || "Não foi possível carregar usuários.",
      });
    } finally {
      setLoading(false);
    }
  }, [activeSearch, paginationModel.page, paginationModel.pageSize, dispatch]);

  const loadRolesCatalog = useCallback(async () => {
    try {
      const rolesData = await getRolesWithPermissions();
      setRolesCatalog(rolesData);
    } catch {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Não foi possível carregar perfis do sistema.",
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAdminManager) void loadRolesCatalog();
  }, [isAdminManager, loadRolesCatalog]);

  useEffect(() => {
    if (isAdminManager) void loadUsers();
    else setLoading(false);
  }, [isAdminManager, loadUsers]);

  const stats = useMemo(
    () => computePageStats(users, rowCount, isSearchMode),
    [users, rowCount, isSearchMode],
  );

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setActiveSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setActiveSearch("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const openManageDialog = async (user: SystemUser) => {
    setOpeningUserId(user.id);
    try {
      setSelectedUser(user);
      const detail = await getUserAccessDetail(user.id);
      setSelectedAccess(detail);
      setDialogOpen(true);
    } catch {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Erro ao carregar acesso do usuário.",
      });
    } finally {
      setOpeningUserId(null);
    }
  };

  const columns: GridColDef<SystemUser>[] = useMemo(
    () => [
      {
        field: "nome",
        headerName: "Usuário",
        flex: 1.2,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<SystemUser>) => (
          <Box minWidth={0}>
            <MDTypography variant="button" fontWeight="medium" noWrap>
              {params.row.nome}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary" display="block" noWrap>
              {params.row.email}
            </MDTypography>
          </Box>
        ),
      },
      {
        field: "cpf",
        headerName: "CPF",
        width: 130,
        renderCell: (params: GridRenderCellParams<SystemUser>) => (
          <MDTypography variant="caption">
            {params.row.cpf ? cpfApplyMask(String(params.row.cpf)) : "—"}
          </MDTypography>
        ),
      },
      {
        field: "roles",
        headerName: "Perfis",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        renderCell: (params: GridRenderCellParams<SystemUser>) => {
          const types = roleTypesFromUser(params.row);
          if (!types.length) {
            return (
              <MDTypography variant="caption" color="text.secondary">
                —
              </MDTypography>
            );
          }
          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5} py={0.5}>
              {types.map((type) => {
                const chip = roleChipSx(type);
                return (
                  <Chip
                    key={type}
                    label={labelRole(type)}
                    size="small"
                    sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }}
                  />
                );
              })}
            </Stack>
          );
        },
      },
      {
        field: "painel",
        headerName: "Painel",
        width: 100,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<SystemUser>) => {
          const hasPanel = userHasPanelAccess(roleTypesFromUser(params.row));
          return (
            <Chip
              label={hasPanel ? "Sim" : "Não"}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: hasPanel ? "rgba(16, 185, 129, 0.12)" : "grey.200",
                color: hasPanel ? "success.dark" : "text.secondary",
              }}
            />
          );
        },
      },
      {
        field: "acoes",
        headerName: "Ações",
        width: 90,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams<SystemUser>) => (
          <Tooltip title="Gerenciar acesso">
            <span>
              <IconButton
                size="small"
                onClick={() => void openManageDialog(params.row)}
                disabled={openingUserId === params.row.id}
                sx={actionIconSx}
              >
                <ManageAccountsIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [openingUserId],
  );

  if (!isAdminManager) {
    return (
      <LojaPageContainer
        title="Controle de acesso"
        subtitle="Gerenciamento restrito a administradores."
      >
        <MDBox sx={emptyStateSx}>
          <LockOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Acesso negado
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Apenas administradores ({labelRole(ROLES.ROLE_ADMIN)}) podem gerenciar controle de
            acesso.
          </MDTypography>
        </MDBox>
      </LojaPageContainer>
    );
  }

  return (
    <LojaPageContainer
      title="Controle de acesso"
      subtitle="Usuários com acesso ao painel. Busque por nome, e-mail ou CPF para atribuir perfis."
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={activeSearch ? "Resultados" : "Total de registros"}
            value={stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Na página" value={stats.pagina} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Com painel (página)" value={stats.comPainel} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Perfis no sistema" value={rolesCatalog.length} />
        </Grid>
      </Grid>

      <MDBox sx={acessoToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SecurityOutlinedIcon sx={{ color: ACESSO_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isSearchMode
                ? `Busca ativa${activeSearch ? `: “${activeSearch}”` : ""} — inclui atletas para atribuição`
                : "Listando usuários operacionais com acesso ao painel"}
            </MDTypography>
          </Stack>

          {!isSearchMode ? (
            <MDBox sx={infoBannerSx}>
              <MDTypography variant="caption" color="text.secondary">
                Use a busca para localizar atletas e demais usuários que ainda não aparecem na
                lista padrão.
              </MDTypography>
            </MDBox>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }}>
            <FormControl variant="outlined" size="small" fullWidth sx={fieldSx}>
              <InputLabel shrink htmlFor="acesso-search">
                Buscar
              </InputLabel>
              <OutlinedInput
                id="acesso-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                label="Buscar"
                notched
                placeholder="Nome, e-mail ou CPF…"
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                }
              />
            </FormControl>
            <MDButton
              variant="gradient"
              color="dark"
              onClick={handleSearch}
              sx={{ ...saveButtonSx, flexShrink: 0, minWidth: { sm: 120 } }}
            >
              Buscar
            </MDButton>
            {activeSearch ? (
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleClearSearch}
                sx={{ flexShrink: 0 }}
              >
                Limpar
              </MDButton>
            ) : null}
          </Stack>
        </Stack>
      </MDBox>

      {!loading && users.length === 0 ? (
        <MDBox sx={emptyStateSx}>
          <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhum usuário encontrado
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            {activeSearch
              ? "Ajuste os termos da busca ou limpe o filtro."
              : "Não há usuários operacionais com acesso ao painel no momento."}
          </MDTypography>
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={users}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={gridSx}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
        />
      )}

      <UserAccessDialog
        open={dialogOpen}
        user={selectedUser}
        rolesCatalog={rolesCatalog}
        initialAccess={selectedAccess}
        allowProtectedRoles={access.isSuperAdmin}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          void loadUsers();
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Acesso do usuário atualizado com sucesso.",
          });
        }}
      />
    </LojaPageContainer>
  );
}
